import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

// ============================================================================
// SettlementPage — Store-facing settlement ledger
// Upcoming card + list + detail modal + CSV export (client-side)
// ============================================================================

const formatWon = (amount) => '₩' + Number(amount || 0).toLocaleString('ko-KR');

const formatDate = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch { return iso; }
};

const formatRange = (start, end) => `${formatDate(start)} ~ ${formatDate(end)}`;

const STATUS_META = {
  pending:      { label: '대기',    color: 'var(--warning)', bg: 'var(--warning-light)' },
  approved:     { label: '승인',    color: 'var(--info)',    bg: 'var(--info-light)' },
  transferring: { label: '송금중',  color: 'var(--info)',    bg: 'var(--info-light)' },
  completed:    { label: '송금완료', color: 'var(--success)', bg: 'var(--success-light)' },
  failed:       { label: '실패',    color: 'var(--danger)',  bg: 'var(--danger-light)' },
};

const FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'pending', label: '대기' },
  { key: 'completed', label: '송금완료' },
];

let toastCounter = 0;
const useToast = () => {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'info') => {
    const id = ++toastCounter;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);
  return { toasts, addToast };
};

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        color: m.color,
        background: m.bg,
      }}
    >
      {m.label}
    </span>
  );
};

// ---------- Detail modal ----------
const DetailModal = ({ settlement, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/admin/settlement/me/${settlement.id}`);
        if (!cancelled) setDetail(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [settlement.id]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 20, maxWidth: 720, width: '100%',
          maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ padding: 24, borderBottom: '1px solid var(--divider)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>정산 상세</h3>
            <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: 13 }}>
              {formatRange(settlement.period_start, settlement.period_end)}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', fontSize: 24, color: 'var(--text-tertiary)' }}>×</button>
        </div>

        <div style={{ padding: 24 }}>
          {loading && <div style={{ color: 'var(--text-tertiary)', textAlign: 'center' }}>불러오는 중...</div>}
          {detail && (
            <>
              {/* Summary grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
                <Stat label="총 매출" value={formatWon(detail.settlement.gross_amount)} />
                <Stat label="PG 수수료" value={`- ${formatWon(detail.settlement.pg_fee)}`} color="var(--danger)" />
                <Stat label="플랫폼 수수료" value={`- ${formatWon(detail.settlement.platform_fee)}`} color="var(--danger)" />
                <Stat label="환불" value={`- ${formatWon(detail.settlement.refund_amount)}`} color="var(--danger)" />
                <div style={{ gridColumn: '1 / -1' }}>
                  <Stat label="실 송금액" value={formatWon(detail.settlement.net_amount)} color="var(--primary)" big />
                </div>
              </div>

              {/* Bank info */}
              <div style={{ padding: 14, background: 'var(--bg)', borderRadius: 12, marginBottom: 20 }}>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>입금 계좌</div>
                <div style={{ fontWeight: 600 }}>
                  {detail.settlement.bank_name || '-'} {detail.settlement.bank_account_number || ''}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  예금주: {detail.settlement.bank_account_holder || '-'}
                </div>
                {detail.settlement.transfer_completed_at && (
                  <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 6 }}>
                    송금완료: {formatDate(detail.settlement.transfer_completed_at)}
                    {detail.settlement.transfer_reference && ` (${detail.settlement.transfer_reference})`}
                  </div>
                )}
              </div>

              {/* Orders */}
              <h4 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 700 }}>포함된 주문 ({detail.orders.length}건)</h4>
              <div style={{ maxHeight: 220, overflow: 'auto', border: '1px solid var(--divider)', borderRadius: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg)' }}>
                    <tr>
                      <th style={thStyle}>주문일시</th>
                      <th style={thStyle}>테이블</th>
                      <th style={thStyleR}>금액</th>
                      <th style={thStyle}>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.orders.length === 0 && (
                      <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: 'var(--text-tertiary)' }}>주문 없음</td></tr>
                    )}
                    {detail.orders.map((o) => (
                      <tr key={o.id}>
                        <td style={tdStyle}>{formatDate(o.created_at)}</td>
                        <td style={tdStyle}>{o.table_number || '-'}</td>
                        <td style={tdStyleR}>{formatWon(o.total_amount)}</td>
                        <td style={tdStyle}>{o.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const thStyle = { padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-tertiary)', borderBottom: '1px solid var(--divider)' };
const thStyleR = { ...thStyle, textAlign: 'right' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid var(--divider)' };
const tdStyleR = { ...tdStyle, textAlign: 'right', fontWeight: 600 };

const Stat = ({ label, value, color, big }) => (
  <div style={{
    padding: 14,
    background: 'var(--bg)',
    borderRadius: 12,
  }}>
    <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: big ? 22 : 16, fontWeight: big ? 800 : 700, color: color || 'var(--text)' }}>
      {value}
    </div>
  </div>
);

// ---------- Main page ----------
const SettlementPage = () => {
  const { toasts, addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [settlements, setSettlements] = useState([]);
  const [totals, setTotals] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [listRes, upRes] = await Promise.all([
        api.get('/admin/settlement/me', { params: filter !== 'all' ? { status: filter } : {} }),
        api.get('/admin/settlement/me/upcoming'),
      ]);
      setSettlements(listRes.data.settlements || []);
      setTotals(listRes.data.totals || null);
      setUpcoming(upRes.data.upcoming || null);
    } catch (e) {
      console.error(e);
      addToast('정산 내역을 불러오지 못했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, addToast]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!settlements.length) {
      addToast('내보낼 데이터가 없습니다', 'error');
      return;
    }
    const headers = ['정산기간시작', '정산기간종료', '매출', 'PG수수료', '플랫폼수수료', '환불', '송금액', '주문수', '상태', '송금일'];
    const rows = settlements.map((s) => [
      formatDate(s.period_start),
      formatDate(s.period_end),
      s.gross_amount,
      s.pg_fee,
      s.platform_fee,
      s.refund_amount,
      s.net_amount,
      s.order_count,
      STATUS_META[s.status]?.label || s.status,
      s.transfer_completed_at ? formatDate(s.transfer_completed_at) : '',
    ]);
    // Prepend BOM for Excel Korean support
    const csv = '\ufeff' + [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `settlements_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addToast('CSV 파일을 다운로드했습니다', 'success');
  };

  return (
    <div style={{ padding: '32px 28px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Toasts */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.type}`}>
            <div className="toast-content"><div className="toast-message">{t.msg}</div></div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', margin: 0 }}>정산 관리</h1>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 14, marginTop: 6 }}>
            매출에서 수수료를 제외한 금액이 지정된 계좌로 송금됩니다.
          </p>
        </div>
        <button
          onClick={exportCSV}
          style={{
            padding: '11px 20px',
            background: '#fff',
            color: 'var(--primary)',
            border: '1.5px solid var(--primary)',
            borderRadius: 12,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Excel (CSV) 내보내기
        </button>
      </div>

      {/* Upcoming card */}
      {upcoming && (
        <div style={{
          background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
          color: '#fff',
          borderRadius: 20,
          padding: 26,
          marginBottom: 24,
          boxShadow: '0 8px 24px rgba(191,155,48,0.25)',
        }}>
          <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 8 }}>다음 정산 예정</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 32, fontWeight: 800 }}>{formatWon(upcoming.net_amount)}</div>
            <div style={{ fontSize: 13, opacity: 0.9 }}>
              매출 {formatWon(upcoming.gross_amount)} — 수수료 {formatWon(upcoming.pg_fee + upcoming.platform_fee)} — 환불 {formatWon(upcoming.refund_amount)}
            </div>
          </div>
          <div style={{ fontSize: 12, marginTop: 10, opacity: 0.85 }}>
            {formatRange(upcoming.period_start, upcoming.period_end)} · 정산주기: {upcoming.cycle} · 주문 {upcoming.order_count}건
          </div>
        </div>
      )}

      {/* Totals */}
      {totals && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Stat label="누적 매출" value={formatWon(totals.total_gross)} />
          <Stat label="누적 수수료" value={formatWon((totals.total_pg || 0) + (totals.total_platform || 0))} color="var(--danger)" />
          <Stat label="누적 환불" value={formatWon(totals.total_refunds)} color="var(--danger)" />
          <Stat label="누적 송금액" value={formatWon(totals.total_net)} color="var(--primary)" />
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={{
              padding: '9px 18px',
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              border: 'none',
              background: filter === f.key ? 'var(--primary)' : '#fff',
              color: filter === f.key ? '#fff' : 'var(--text-secondary)',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 800 }}>
            <thead style={{ background: 'var(--bg)' }}>
              <tr>
                <th style={thStyle}>정산기간</th>
                <th style={thStyleR}>매출</th>
                <th style={thStyleR}>수수료</th>
                <th style={thStyleR}>환불</th>
                <th style={thStyleR}>송금액</th>
                <th style={thStyle}>상태</th>
                <th style={thStyle}>송금일</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>불러오는 중...</td></tr>
              )}
              {!loading && settlements.length === 0 && (
                <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: 'var(--text-tertiary)' }}>정산 내역이 없습니다</td></tr>
              )}
              {!loading && settlements.map((s) => (
                <tr
                  key={s.id}
                  onClick={() => setSelected(s)}
                  style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={tdStyle}>{formatRange(s.period_start, s.period_end)}</td>
                  <td style={tdStyleR}>{formatWon(s.gross_amount)}</td>
                  <td style={{ ...tdStyleR, color: 'var(--danger)' }}>
                    {formatWon((s.pg_fee || 0) + (s.platform_fee || 0))}
                  </td>
                  <td style={{ ...tdStyleR, color: 'var(--danger)' }}>{formatWon(s.refund_amount)}</td>
                  <td style={{ ...tdStyleR, color: 'var(--primary)' }}>{formatWon(s.net_amount)}</td>
                  <td style={tdStyle}><StatusPill status={s.status} /></td>
                  <td style={tdStyle}>{s.transfer_completed_at ? formatDate(s.transfer_completed_at) : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailModal settlement={selected} onClose={() => setSelected(null)} />}
    </div>
  );
};

export default SettlementPage;
